import React, { useEffect, useMemo, useState, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Minus, Check, X, LogOut, AlertCircle, Search } from 'lucide-react';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement } from '../../lib/table-hooks';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { TableWithOrder, MenuItem, CartItemOption } from '../../lib/types';
import { createNatuCafeAdditionPrintJob, createRanchoAdditionPrintJob } from '../../lib/print-additions';
import { formatCurrency, cn, aggregateQuantityOptions } from '../../lib/utils';
import { ProductDetailModal } from '../../components/restaurant/ProductDetailModal';
import { Button } from '../../components/ui/Button';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ArmaTuPlatoPanel } from '../../components/restaurant/ArmaTuPlatoPanel';
import { EmiliusArmaTuPizzaModal } from '../../components/restaurant/EmiliusArmaTuPizzaModal';
// ─── MS Express external delivery catalog row (flexible shape) ──
interface MsExpressZoneRow {
  id: string | null;
  zone: string;
  subzone: string;
  fee: number;
}
function resolveMsExpressRow(r: any): MsExpressZoneRow | null {
  const zone = String(r?.zone ?? r?.zone_name ?? r?.provincia ?? '').trim();
  const subzone = String(r?.subzone ?? r?.subzone_name ?? r?.localidad ?? r?.locality ?? '').trim();
  const feeRaw = r?.delivery_fee ?? r?.fee ?? r?.price ?? r?.cost ?? r?.precio ?? 0;
  const fee = Number(feeRaw) || 0;
  if (!zone || !subzone) return null;
  return {
    id: r?.id != null ? String(r.id) : null,
    zone,
    subzone,
    fee
  };
}
interface WaiterCartItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
// La Cabaña Parrillera (restaurant_id 24) — Tomar Orden supports
// three modes: Mesa (default, like other restaurants), Para llevar,
// and Express (con datos del cliente, por llamada telefónica).
type WaiterOrderMode = 'mesa' | 'pickup' | 'delivery';
interface WaiterCustomerInfo {
  name: string;
  phone: string;
  address: string;
  maps_url: string;
  notes: string;
  // Cabaña Parrillera — optional express fee (monto del express).
  // Stored as a string while editing so the input stays controllable;
  // converted to a number before submitting / totalizing.
  express_fee: string;
}
const EMPTY_CUSTOMER_INFO: WaiterCustomerInfo = {
  name: '',
  phone: '',
  address: '',
  maps_url: '',
  notes: '',
  express_fee: ''
};
const STORAGE_KEY_PREFIX = 'noiza_waiter_order_';
function getStorageKey(restaurantId: string, mode: WaiterOrderMode, tableId?: string | null) {
  if (mode === 'mesa' && tableId) {
    return `${STORAGE_KEY_PREFIX}${restaurantId}_${tableId}`;
  }
  return `${STORAGE_KEY_PREFIX}${restaurantId}_${mode}`;
}
function loadPersistedState(restaurantId: string | undefined, mode: WaiterOrderMode, tableId?: string | null) {
  if (!restaurantId || typeof window === 'undefined') return null;
  try {
    const key = getStorageKey(restaurantId, mode, tableId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as {
      cart: WaiterCartItem[];
      customerInfo?: WaiterCustomerInfo;
    };
  } catch {
    return null;
  }
}
export function WaiterPOSPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const restaurantId = restaurant?.restaurant_id;
  const {
    tables,
    loading: tablesLoading
  } = useTableManagement(restaurantId);
  const {
    categories
  } = useMenuCategories(restaurantId);
  const {
    items: rawItems
  } = useMenuItems(restaurantId);
  // Qué productos puede agregar el mesero al tomar una orden.
  //
  // Los productos de Estantería / "Tienda / Productos de caja"
  // (product_type='retail', o station_code='estanteria') NO se muestran
  // acá. Motivo real, detectado en NatuCafé: esos productos están
  // configurados para NO imprimirse en comanda (el puente los descarta
  // a propósito), así que si el mesero elige uno por error — por
  // ejemplo "SANDIA" de estantería en lugar del batido de sabor
  // sandía — el producto nunca sale en el papel y la barra jamás lo ve.
  // Se venden únicamente desde Caja → Estantería, donde sí existe el
  // flujo correcto (venta rápida y "Agregar a mesa activa").
  //
  // Se excluyen:
  //  · `product_type === 'retail'` y `station_code === 'estanteria'`;
  //  · `internal_only` — insumos internos, nunca se venden;
  //  · `show_in_pos === false` — ocultos a propósito del punto de venta;
  //  · `show_in_digital_menu === false`.
  // Defensivo: tolera columnas faltantes en DBs legacy.
  const items = useMemo(() => (rawItems || []).filter((it: any) => {
    if (it?.internal_only === true) return false;
    if (it?.show_in_pos === false) return false;
    if (it?.show_in_digital_menu === false) return false;
    if (String(it?.product_type || '').toLowerCase() === 'retail') {
      return false;
    }
    if (String(it?.station_code || '').trim().toLowerCase() === 'estanteria') {
      return false;
    }
    return true;
  }), [rawItems]);
  // Orden de las mesas en la grilla. La fuente (`tables`) puede venir
  // ordenada como texto ("1","10","11",…,"2"), lo que en el celular se ve
  // como 1, 10, 11, 12… 2 — desordenado. Acá ordenamos numéricamente por
  // `table_number` cuando es numérico; si no lo es (ej. "Barra 5") caemos
  // a una comparación natural por locale. Es SOLO orden de presentación:
  // no toca datos ni el flujo de selección. Aplica a todos los
  // restaurantes por igual.
  const sortedTables = useMemo(() => {
    const arr = [...(tables || [])];
    arr.sort((a, b) => {
      const na = Number(a.table_number);
      const nb = Number(b.table_number);
      const aNum = !Number.isNaN(na);
      const bNum = !Number.isNaN(nb);
      if (aNum && bNum) return na - nb;
      if (aNum) return -1;
      if (bNum) return 1;
      return String(a.table_number).localeCompare(String(b.table_number), 'es', {
        numeric: true
      });
    });
    return arr;
  }, [tables]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  // ─── orderMode initializer with self-healing ──────────────────────
  const [orderMode, setOrderMode] = useState<WaiterOrderMode>('mesa');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<WaiterCartItem[]>([]);
  const [customerInfo, setCustomerInfo] = useState<WaiterCustomerInfo>(EMPTY_CUSTOMER_INFO);
  const [showEmiliusArmaTuPizzaModal, setShowEmiliusArmaTuPizzaModal] = useState(false);
  // Load persisted state when table or mode changes
  useEffect(() => {
    if (!restaurantId) return;
    const effectiveMode = selectedTable ? 'mesa' : orderMode;
    const persisted = loadPersistedState(restaurantId, effectiveMode, selectedTable?.id);
    if (persisted) {
      setCart(persisted.cart || []);
      if (persisted.customerInfo) {
        setCustomerInfo(persisted.customerInfo);
      }
    } else {
      setCart([]);
      setCustomerInfo(EMPTY_CUSTOMER_INFO);
    }
  }, [restaurantId, selectedTable?.id, orderMode]);
  // ─── Arma tu plato (Rancho San Francisco, id 28) ──────────────────
  // Tab sintético al final de las categorías. Permite al mesero
  // escribir líneas libres de texto (ej. "Arroz", "4 chicharrones",
  // "Ensalada", "Papas") que se agregan al pedido como ítems
  // personalizados sin menu_item_id. La comanda en cocina los muestra
  // como cualquier otra línea — útil para platos armados a pedido.
  //
  // Implementación: cada línea es un WaiterCartItem con un MenuItem
  // sintético (id con prefijo `__custom__`, price 0). En el submit
  // (tanto edge function como merge directo) detectamos el prefijo y
  // mandamos `menu_item_id: null` — create-order.ts (línea 330) acepta
  // null para este campo. Scope: SOLO Rancho. Ningún otro restaurante
  // ve este tab.
  const [armaLines, setArmaLines] = useState<string[]>(['', '', '', '', '', '']);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Anti-doble-click ref. Espejo síncrono de isSubmitting que no depende
  // del ciclo de render de React — el setState de isSubmitting no se
  // refleja en la siguiente ejecución del handler si el segundo click
  // entra antes del próximo render. submittingRef cierra esa ventana.
  // Mismo patrón que CheckoutPage.handleSubmit (lib pattern).
  const submittingRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // ─── Cabaña Parrillera — multi-mode support ──────────────────
  // Only enabled for restaurant_id === '24' / slug 'la-cabana-parrillera'.
  // Every other restaurant keeps the table-only flow it always had.
  const isCabanaParrillera = String(restaurant?.id) === '24' || String(restaurantId) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // Rancho San Francisco (id 28) — después de enviar la orden el dueño
  // quiere volver directo a la pantalla de Mesas (su "home"), no al
  // step 1 / selector de estación. Sólo aplica a este restaurante.
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || String(restaurantId) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // NatuCafé Coffee Shop (id 19) — mismo patrón post-orden que Rancho:
  // volver a Mesas después de enviar para que el flujo "mesa → orden
  // → mesa" sea instantáneo durante el rush. NatuCafé también gana un
  // toque de branding (logo en el top bar). Ningún query se comparte
  // con otros restaurantes — solo banderas de UI scopeadas a id 19.
  const isNatuCafe = String(restaurant?.id) === '19' || String(restaurantId) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  // Emilius Pizza (id 27) — mismo flujo que NatuCafé en Tomar Orden:
  // vuelve a Mesas tras enviar y soporta Mesa / Para llevar / Express.
  // Emilius NO comparte el hideo de categorías ni el orden custom de
  // NatuCafé — usa sus categorías tal cual. NatuCafé NO se ve afectado.
  const isEmilius = String(restaurant?.id) === '27' || String(restaurantId) === '27' || restaurant?.slug === 'emilius-pizza';
  const returnsToTablesAfterSubmit = isRanchoSanFrancisco || isNatuCafe || isEmilius;
  // Multi-mode (Mesa / Para llevar / Express) — habilitado para Cabaña
  // Parrillera, NatuCafé, Rancho San Francisco y Emilius. Cualquier otra
  // ubicación sigue con el flujo clásico de solo-mesa.
  const supportsMultiMode = isCabanaParrillera || isNatuCafe || isRanchoSanFrancisco || isEmilius;
  // ─── MS Express external delivery (NatuCafé) ──────────────────────────
  const [msZones, setMsZones] = useState<MsExpressZoneRow[]>([]);
  const [msZonesLoading, setMsZonesLoading] = useState(false);
  const [selectedMsZone, setSelectedMsZone] = useState<string>('');
  const [selectedMsSubzone, setSelectedMsSubzone] = useState<string>('');
  const [selectedMsFee, setSelectedMsFee] = useState<number>(0);
  const [selectedMsCatalogId, setSelectedMsCatalogId] = useState<string | null>(null);
  // Load zones only when needed
  useEffect(() => {
    if (!isNatuCafe || orderMode !== 'delivery') return;
    let cancelled = false;
    const loadZones = async () => {
      setMsZonesLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('delivery_zones_catalog').select('id, zone, subzone, price').eq('provider_name', 'MS Express').is('restaurant_id', null).eq('is_active', true).order('zone', {
          ascending: true
        }).order('price', {
          ascending: true
        }).order('subzone', {
          ascending: true
        });
        if (error) {
          console.error('[MS_EXPRESS] delivery_zones_catalog error:', error);
          if (!cancelled) setMsZones([]);
          return;
        }
        const rows = (data || []).map(resolveMsExpressRow).filter((r): r is MsExpressZoneRow => r !== null);
        if (!cancelled) setMsZones(rows);
      } catch (err) {
        console.error('[MS_EXPRESS] load zones exception:', err);
        if (!cancelled) setMsZones([]);
      } finally {
        if (!cancelled) setMsZonesLoading(false);
      }
    };
    loadZones();
    return () => {
      cancelled = true;
    };
  }, [isNatuCafe, orderMode]);
  // Reset MS Express selection when leaving delivery
  useEffect(() => {
    if (orderMode !== 'delivery') {
      setSelectedMsZone('');
      setSelectedMsSubzone('');
      setSelectedMsFee(0);
      setSelectedMsCatalogId(null);
    }
  }, [orderMode]);
  const msZoneNames = useMemo(() => Array.from(new Set(msZones.map(z => z.zone))).sort((a, b) => a.localeCompare(b, 'es')), [msZones]);
  const msSubzonesForZone = useMemo(() => msZones.filter(z => z.zone === selectedMsZone).sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.subzone.localeCompare(b.subzone, 'es');
  }), [msZones, selectedMsZone]);
  // Persist whenever cart or customer info changes
  useEffect(() => {
    if (!restaurantId || typeof window === 'undefined') return;
    const effectiveMode = selectedTable ? 'mesa' : orderMode;
    const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
    const isEmptyCustomer = !customerInfo.name && !customerInfo.phone && !customerInfo.address && !customerInfo.notes;
    if (cart.length === 0 && isEmptyCustomer) {
      localStorage.removeItem(key);
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        cart,
        customerInfo
      }));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }, [restaurantId, selectedTable, cart, orderMode, customerInfo]);
  // Filter active categories and items.
  //
  // NatuCafé (id 19) — además del filtro estándar `is_active`, escondemos
  // los "parents" que en esta pantalla quedan vacíos o son redundantes:
  // "Bebidas y Repostería" y "Comida". Sus items reales viven en las
  // sub-categorías (Bebidas calientes, Bebidas frías, Repostería,
  // Sánguches, Platos fuertes, etc.) que sí se muestran. Esta es una
  // capa visual SOLO en "Tomar Orden" — no se borra nada de la DB, y el
  // menú público / KDS siguen viendo todas las categorías como siempre.
  const hiddenWaiterCategoryNames = useMemo(() => {
    if (!isNatuCafe) return new Set<string>();
    return new Set(['bebidas y reposteria', 'bebidas y repostería', 'comida'].map(s => s.toLowerCase()));
  }, [isNatuCafe]);
  // NatuCafé (id 19) — el menú público agrupa las subcategorías en dos
  // "parents": 'bebidas' (Bebidas y Repostería) y 'comida'. Dentro de cada
  // grupo el orden es por DB sort_order. En "Tomar Orden" debemos verlas
  // EXACTAMENTE en el mismo orden, no mezcladas alfabéticamente ni por
  // sort_order global. Esta lógica es 1:1 con RestaurantPage > getNatuCafeParentKey.
  // Scope: SOLO restaurant id 19 / slug natucafe-coffee-shop. Ningún otro
  // restaurante toca esta rama.
  const getNatuCafeParent = (name: string): 'bebidas' | 'comida' => {
    const n = (name || '').toLowerCase().trim();
    const bebidasSubs = ['bebidas calientes', 'bebidas frías', 'bebidas frias', 'té y más', 'te y mas', 'aguadulce', 'batidos', 'batidos en agua', 'batidos con leche', 'postres', 'repostería', 'reposteria'];
    if (bebidasSubs.some(sub => n.includes(sub) || sub.includes(n))) {
      return 'bebidas';
    }
    return 'comida';
  };
  // Categorías que quedaron sin ningún producto visible para el mesero.
  // Al excluir Estantería, la pestaña "Tienda / Productos de caja" queda
  // vacía y no debe seguir ofreciéndose. Sólo se calcula cuando ya
  // cargaron productos, para no vaciar la barra de categorías mientras
  // el menú está cargando.
  const categoryIdsWithItems = useMemo(() => {
    const ids = new Set<string>();
    for (const it of items || []) {
      if ((it as any)?.is_active === false) continue;
      if ((it as any)?.category_id) ids.add(String((it as any).category_id));
    }
    return ids;
  }, [items]);
  const activeCategories = useMemo(() => {
    const filtered = categories.filter(c => c.is_active).filter(c => !hiddenWaiterCategoryNames.has((c.name || '').trim().toLowerCase())).filter(c => categoryIdsWithItems.size === 0 || categoryIdsWithItems.has(String(c.id)));
    if (isNatuCafe) {
      const bebidas = filtered.filter(c => getNatuCafeParent(c.name || '') === 'bebidas').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const comida = filtered.filter(c => getNatuCafeParent(c.name || '') === 'comida').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      return [...bebidas, ...comida];
    }
    return filtered.sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, hiddenWaiterCategoryNames, isNatuCafe, categoryIdsWithItems]);
  // Append sintético "Arma tu plato" al final — Rancho (siempre) +
  // NatuCafé (sólo en modo Mesa). Scope quirúrgico: el tab NO aparece en
  // marketplace, QR auto-atención, ni cuando un restaurante usa flujos
  // pickup/delivery. La infra para items con menu_item_id=NULL ya es
  // restaurant-agnostic (create-order.ts, PayTableModal, bridge,
  // triggers de print/station_code), así que NatuCafé reusa el mismo
  // pipeline sin cambios adicionales.
  const ARMA_TU_PLATO_ID = '__arma_tu_plato__';
  const armaTuPlatoEnabled = isRanchoSanFrancisco || isNatuCafe && orderMode === 'mesa';
  const displayCategories = useMemo(() => {
    if (!armaTuPlatoEnabled) return activeCategories;
    return [...activeCategories, {
      id: ARMA_TU_PLATO_ID,
      name: 'Arma tu plato',
      is_active: true,
      sort_order: 9999
    } as any];
  }, [activeCategories, armaTuPlatoEnabled]);
  const activeItems = useMemo(() => items.filter(i => i.is_active).sort((a, b) => a.sort_order - b.sort_order), [items]);
  // Set initial category
  useEffect(() => {
    if (activeCategories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [activeCategories, activeCategoryId]);
  // ─── Defensa: si el tab Arma tu plato se desactiva (ej. NatuCafé cambia
  // de Mesa → Para llevar mientras estaba abierto el tab), reseteamos
  // activeCategoryId a la primera categoría real. Sin esto el grid queda
  // mostrando "No hay productos" — funciona pero confunde al cajero.
  useEffect(() => {
    if (activeCategoryId === ARMA_TU_PLATO_ID && !armaTuPlatoEnabled && activeCategories.length > 0) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [armaTuPlatoEnabled, activeCategoryId, activeCategories]);
  const cartTotal = cart.reduce((sum, item) => {
    const optionsTotal = item.options.reduce((optSum, opt) => optSum + (Number(opt.price_delta) || 0), 0);
    return sum + (Number(item.menuItem.price) + optionsTotal) * item.quantity;
  }, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  // Cabaña Parrillera — Express fee adds to total only for delivery mode.
  // NatuCafé — uses selectedMsFee instead of manual input.
  const expressFeeAmount = supportsMultiMode && orderMode === 'delivery' ? isNatuCafe ? selectedMsFee : Math.max(0, Number(customerInfo.express_fee) || 0) : 0;
  const orderTotal = cartTotal + expressFeeAmount;
  const handleTableSelect = (table: TableWithOrder) => {
    setSelectedTable(table);
    setStep(2);
  };
  // ─── Auto-select table from `?mesa=<table_number>` URL param ──────
  // Used by Rancho's Mesas grid (and any other restaurant in the future)
  // to deep-link straight into the "Tomar Orden" flow for a specific
  // mesa. Runs ONCE when tables are loaded and the URL param is present.
  const autoSelectedRef = useRef(false);
  // ─── Auto-select orderMode from `?mode=pickup|delivery|mesa` ─────
  // Used by NatuCafé's TableManagementPage tipo-de-orden cards para
  // deep-link al flujo correcto. Solo aplica si el restaurante
  // soporta multi-mode; el resto ignora el param.
  const modeSelectedRef = useRef(false);
  useEffect(() => {
    if (modeSelectedRef.current) return;
    if (!supportsMultiMode) return;
    const modeParam = searchParams.get('mode');
    if (modeParam === 'pickup' || modeParam === 'delivery') {
      modeSelectedRef.current = true;
      setOrderMode(modeParam as WaiterOrderMode);
      setSelectedTable(null);
      // CRÍTICO: para "Para llevar" (pickup) y "Express" (delivery) el
      // paso 1 es el formulario de DATOS DEL CLIENTE (nombre, teléfono,
      // dirección). NO saltar a step 2 (menú) — eso se salta la captura
      // de datos. Aterrizamos en step 1 para que el cajero llene los
      // datos primero y de ahí avance al menú con "Continuar".
      // (Las mesas SÍ van directo a step 2 porque no tienen este
      //  formulario — ver el deep-link ?mesa=xxx más abajo.)
      setStep(1);
      // Remove from URL so it doesn't stick if they go back
      const next = new URLSearchParams(searchParams);
      next.delete('mode');
      setSearchParams(next, {
        replace: true
      });
    }
  }, [supportsMultiMode, searchParams, setSearchParams]);
  // If URL has ?mesa=xxx, auto-select it and go to step 2
  useEffect(() => {
    if (tablesLoading || tables.length === 0) return;
    const mesaParam = searchParams.get('mesa');
    if (mesaParam) {
      const found = tables.find(t => t.id === mesaParam || String(t.table_number) === mesaParam || t.table_name?.toLowerCase() === mesaParam.toLowerCase());
      if (found) {
        setSelectedTable(found);
        setOrderMode('mesa');
        setStep(2);
      }
      // Remove from URL so it doesn't stick if they go back
      const next = new URLSearchParams(searchParams);
      next.delete('mesa');
      setSearchParams(next, {
        replace: true
      });
    }
  }, [tables, tablesLoading, searchParams, setSearchParams]);
  const handleAddToCart = (menuItem: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: crypto.randomUUID(),
      menuItem,
      quantity,
      options,
      notes
    }]);
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? {
          ...item,
          quantity: newQty
        } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  const removeItem = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || cart.length === 0) return;
    // ─── Effective mode (anti-orphan guard, pre-validation) ─────────
    // Si hay una mesa seleccionada, la orden es SIEMPRE mesa, sin
    // importar lo que diga orderMode. Esto evita que un estado
    // inconsistente (orderMode='delivery' con selectedTable set,
    // típicamente por localStorage stale) bloquee al usuario con
    // "La dirección es requerida" o cree una orden delivery sin
    // table_id. Las validaciones de abajo usan effectiveMode, el
    // payload de submit también — todo alineado en un solo lugar.
    const effectiveMode: WaiterOrderMode = selectedTable ? 'mesa' : orderMode;
    // Mesa requires a real table; pickup/delivery don't
    if (effectiveMode === 'mesa' && !selectedTable) return;
    if (effectiveMode === 'delivery' && !customerInfo.address.trim()) {
      setSubmitError('La dirección es requerida para pedidos express.');
      return;
    }
    if (isNatuCafe && effectiveMode === 'delivery' && (!selectedMsZone || !selectedMsSubzone)) {
      setSubmitError('Debe seleccionar la zona y localidad de entrega.');
      return;
    }
    if ((effectiveMode === 'pickup' || effectiveMode === 'delivery') && !customerInfo.name.trim()) {
      setSubmitError('El nombre del cliente es requerido.');
      return;
    }
    // ─── Anti-doble-click hard gate ─────────────────────────────────
    // Si una llamada anterior todavía está en vuelo, descartamos esta
    // SILENCIOSAMENTE (sin tocar estados ni mostrar errores). Esto
    // bloquea el caso de dos clicks rapidísimos antes de que React
    // refleje setIsSubmitting(true) en el próximo render. Sin este
    // gate, el segundo click podía generar un order_intent_id distinto
    // y crear DOS órdenes en paralelo (el fingerprint MD5 atrapa la
    // mayoría, pero esto cierra la ventana en origen).
    //
    // Colocado DESPUÉS de las validaciones (no antes) para que validación
    // fallida no deje el ref bloqueado. Cuando una validación retorna
    // temprano, el ref nunca se setea — el siguiente click puede entrar
    // normal una vez que el usuario corrija el form.
    if (submittingRef.current) {
      console.warn('[WAITER POS] submit ignored — already in flight');
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // ─── La Cabaña Parrillera — merge into existing open table order ───
      // When a table is already occupied with an open order, adding more
      // products from "Tomar Orden" must APPEND to that order, not create
      // a second one. Mirrors CabanaAddItemsModal logic so the KDS sees
      // the new items inside the same comanda.
      const existingOrderId = supportsMultiMode && effectiveMode === 'mesa' && selectedTable?.current_order?.id ? selectedTable.current_order.id : null;
      if (existingOrderId) {
        try {
          // VALIDATE order belongs to table before merging
          const {
            data: validateOrder
          } = await supabase.from('orders').select('table_id, restaurant_id').eq('id', existingOrderId).maybeSingle();
          if (!validateOrder || String(validateOrder.table_id) !== String(selectedTable?.id) || String(validateOrder.restaurant_id) !== String(restaurantId)) {
            throw new Error('Esta orden no corresponde a esta mesa. Recargá la mesa.');
          }
          console.log('[NOIZA][SEND_TO_KITCHEN]', {
            restaurantId,
            tableId: selectedTable?.id,
            orderId: existingOrderId,
            orderNumber: selectedTable?.current_order?.order_number
          });
          // 1) Insert each new order_item (same shape as create-order.ts)
          for (const ci of cart) {
            const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
            const unitPrice = Number(ci.menuItem.price) || 0;
            const lineTotal = (unitPrice + optTotal) * ci.quantity;
            const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
            const {
              data: inserted,
              error: itemErr
            } = await supabase.from('order_items').insert({
              order_id: existingOrderId,
              restaurant_id: Number(restaurantId),
              menu_item_id: isCustomArmaItem ? null : ci.menuItem.id,
              item_name_snapshot: ci.menuItem.name,
              unit_price_snapshot: unitPrice,
              quantity: ci.quantity,
              notes: ci.notes || null,
              line_total: lineTotal,
              status: 'pending'
            }).select('id').single();
            if (itemErr) throw itemErr;
            if ((ci.options || []).length > 0 && inserted) {
              const optsPayload = ci.options.map((o: any) => ({
                order_item_id: inserted.id,
                restaurant_id: Number(restaurantId),
                option_group: o.option_group || '',
                option_name: o.option_name || '',
                price_delta: Number(o.price_delta) || 0
              }));
              const {
                error: optErr
              } = await supabase.from('order_item_options').insert(optsPayload);
              if (optErr) {
                console.warn('[WAITER POS][cabana-merge] options insert non-blocking:', optErr);
              }
            }
          }
          // 2) Recompute order subtotal/total from surviving items
          const {
            data: allItems,
            error: fetchErr
          } = await supabase.from('order_items').select('line_total, status').eq('order_id', existingOrderId);
          if (fetchErr) throw fetchErr;
          const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
          const {
            data: orderRow
          } = await supabase.from('orders').select('delivery_fee, status').eq('id', existingOrderId).maybeSingle();
          const deliveryFee = Number(orderRow?.delivery_fee) || 0;
          const newTotal = newSubtotal + deliveryFee;
          const updatePayload: Record<string, any> = {
            subtotal: newSubtotal,
            total: newTotal,
            updated_at: new Date().toISOString()
          };
          // If the order was already marked 'ready', bump it back to
          // 'preparing' so KDS shows it again with the new pending items.
          if (orderRow?.status === 'ready') {
            updatePayload.status = 'preparing';
          }
          const {
            error: updErr
          } = await supabase.from('orders').update(updatePayload).eq('id', existingOrderId);
          if (updErr) throw updErr;
          // ─── NatuCafé (id 19) — adición a mesa abierta ─────────────
          // Mesa ya tenía comanda inicial impresa; el trigger de DB no
          // dispara una segunda vez. Acá enviamos a cocina/barra SOLO
          // los items recién agregados, con la mesa correcta y un
          // bloque 'NOTA: ADICIÓN'. No-bloqueante.
          if (Number(restaurantId) === 19 || String(restaurantId) === '19') {
            try {
              const addedItemsForPrint = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s: number, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
              const result = await createNatuCafeAdditionPrintJob({
                restaurantId,
                orderId: existingOrderId,
                addedItems: addedItemsForPrint
              });
              if (!result.ok) {
                console.warn('[NATUCAFE_ADD_PRINT][WaiterPOS] skipped/failed:', result.reason);
              } else {
                console.log('[NATUCAFE_ADD_PRINT][WaiterPOS] ✅ print job created:', result.jobId);
              }
            } catch (printErr) {
              console.warn('[NATUCAFE_ADD_PRINT][WaiterPOS] exception:', printErr);
            }
          }
          // ─── Rancho San Francisco (id 28) — adición a mesa abierta ──
          // Igual que NatuCafé: la mesa ya tenía comanda inicial. El
          // bridge de Rancho imprime cocina SÓLO desde payload.items, así
          // que mandamos un print job de adición con ÚNICAMENTE los ítems
          // recién agregados (con sus opciones y notas). Esto evita que se
          // reimprima la orden completa y garantiza que el detalle ("de
          // qué es el natural", etc.) salga siempre. No-bloqueante.
          if (Number(restaurantId) === 28 || String(restaurantId) === '28') {
            try {
              const addedItemsForPrint = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s: number, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
              const result = await createRanchoAdditionPrintJob({
                restaurantId,
                orderId: existingOrderId,
                addedItems: addedItemsForPrint
              });
              if (!result.ok) {
                console.warn('[RANCHO_ADD_PRINT][WaiterPOS] skipped/failed:', result.reason);
              } else {
                console.log('[RANCHO_ADD_PRINT][WaiterPOS] ✅ print job created:', result.jobId);
              }
            } catch (printErr) {
              console.warn('[RANCHO_ADD_PRINT][WaiterPOS] exception:', printErr);
            }
          }
        } catch (mergeErr: any) {
          console.error('[WAITER POS][cabana-merge] error:', mergeErr);
          throw new Error(mergeErr?.message || 'No se pudo adicionar a la mesa');
        }
        // Success — same reset flow as the create path below.
        setCart([]);
        setSelectedTable(null);
        setOrderMode('mesa');
        setCustomerInfo(EMPTY_CUSTOMER_INFO);
        setStep(1);
        if (restaurantId && typeof window !== 'undefined') {
          const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
          localStorage.removeItem(key);
        }
        return;
      }
      console.log('[NOIZA][SEND_TO_KITCHEN]', {
        restaurantId,
        tableId: selectedTable?.id,
        orderId: null,
        orderNumber: null
      });
      const orderIntentId = crypto.randomUUID();
      let payload: Record<string, any>;
      // 🔧 STATION ROUTING: cada item del carrito carga consigo el
      // `station_code` configurado en `menu_items` (Editar Producto →
      // Estación de preparación). Lo enviamos EXPLÍCITAMENTE en el
      // payload para que el edge function lo persista en `order_items`
      // y el KDS pueda filtrar por estación. Esto reemplaza la dependencia
      // del trigger de DB (que en algunos restaurantes está como no-op).
      // Cobertura segura: Cabaña / Rancho / NatuCafé / etc.
      //
      // ─── Anti-orphan guard ───────────────────────────────────────
      // BLINDAJE: si effectiveMode === 'mesa' (computado arriba de la
      // misma forma para validación), la orden SIEMPRE se crea como
      // dine_in con table_id. Esto cierra estructuralmente la puerta
      // a las órdenes huérfanas (source=waiter + table_id=null) que el
      // bug viejo de localStorage podía producir.
      if (effectiveMode === 'mesa') {
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: `Mesa ${selectedTable!.table_number}`,
          delivery_type: 'dine_in',
          table_id: selectedTable!.id,
          source: 'waiter',
          subtotal: cartTotal,
          total: cartTotal,
          items: cart.map(item => {
            // Arma tu plato — ítems sintéticos sin menu_item_id real.
            const isCustomArmaItem = typeof item.menuItem.id === 'string' && (item.menuItem.id as string).startsWith('__custom__');
            return {
              menu_item_id: isCustomArmaItem ? null : item.menuItem.id,
              item_name_snapshot: item.menuItem.name,
              unit_price_snapshot: item.menuItem.price,
              quantity: item.quantity,
              notes: item.notes,
              selected_options: item.options,
              station_code: (item.menuItem as any).station_code ?? null
            };
          })
        };
      } else {
        const isDelivery = orderMode === 'delivery';
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: customerInfo.name.trim(),
          customer_phone: customerInfo.phone.trim() || null,
          delivery_type: isDelivery ? 'delivery' : 'pickup',
          table_id: null,
          delivery_address: isDelivery ? customerInfo.address.trim() || null : null,
          delivery_zone: isDelivery && isNatuCafe ? selectedMsZone || null : null,
          delivery_subzone: isDelivery && isNatuCafe ? selectedMsSubzone || null : null,
          delivery_zone_catalog_id: isDelivery && isNatuCafe ? selectedMsCatalogId || null : null,
          maps_url: isDelivery ? customerInfo.maps_url.trim() || null : null,
          notes: customerInfo.notes.trim() || null,
          payment_method: 'cash',
          source: 'waiter',
          subtotal: cartTotal,
          delivery_fee: isDelivery ? expressFeeAmount : 0,
          total: cartTotal + (isDelivery ? expressFeeAmount : 0),
          items: cart.map(item => {
            const isCustomArmaItem = typeof item.menuItem.id === 'string' && (item.menuItem.id as string).startsWith('__custom__');
            return {
              menu_item_id: isCustomArmaItem ? null : item.menuItem.id,
              item_name_snapshot: item.menuItem.name,
              unit_price_snapshot: item.menuItem.price,
              quantity: item.quantity,
              notes: item.notes,
              selected_options: item.options,
              station_code: (item.menuItem as any).station_code ?? null
            };
          })
        };
      }
      console.log('[WAITER POS] orderMode:', orderMode);
      console.log('[WAITER POS] Submitting payload:', payload);
      // BLINDAJE (NatuCafé, 07/08): antes este envío NO tenía tiempo
      // límite. Si la red se colgaba, la petición quedaba esperando para
      // siempre: el botón giraba sin mostrar éxito ni error. Si en ese
      // estado se reiniciaba la tablet, la comanda se perdía SIN NINGÚN
      // AVISO y el mesero quedaba convencido de haberla enviado — la mesa
      // nunca se abría en el sistema y en caja no había nada que cobrar.
      //
      // Con el límite de tiempo, el mesero SIEMPRE recibe una respuesta:
      // o la comanda entró, o se le avisa explícitamente que no.
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), 25000);
      let response: Response;
      try {
        response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify(payload),
          signal: timeoutController.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Error al crear la orden');
      }
      let createdOrderId: string | number | null = result?.order?.id ?? null;
      let createdOrderUuid: string | null = result?.order?.tracking_token ?? null;
      let createdOrderNumber: string | number | null = result?.order?.order_number ?? null;
      if (!createdOrderId || !createdOrderUuid) {
        throw new Error('No se pudo obtener el ID de la orden creada. Por favor, recargá la página.');
      }
      console.log('[NOIZA][TABLE_ORDER_RESOLVED]', {
        restaurantId,
        tableId: selectedTable?.id,
        orderId: createdOrderId,
        orderNumber: createdOrderNumber
      });
      // VALIDATE order belongs to table before proceeding
      if (effectiveMode === 'mesa') {
        const {
          data: validateOrder
        } = await supabase.from('orders').select('table_id, restaurant_id').eq('id', createdOrderId).maybeSingle();
        if (!validateOrder || String(validateOrder.table_id) !== String(selectedTable?.id) || String(validateOrder.restaurant_id) !== String(restaurantId)) {
          throw new Error('Esta orden no corresponde a esta mesa. Recargá la mesa.');
        }
      }
      // ─── Rancho San Francisco (id 28) — Print job (with diagnostics) ─
      // Logs DE DEBUG sin filtro de restaurante para diagnosticar por qué
      // no se está creando la fila en order_print_jobs en producción.
      // Solo el INSERT está gated a Rancho.
      console.log('[RANCHO_PRINT_TABLES] restaurantId:', restaurantId);
      console.log('[RANCHO_PRINT_TABLES] restaurant:', restaurant);
      console.log('[RANCHO_PRINT_TABLES] order response:', result);
      const isRancho28 = Number(restaurantId) === 28 || Number(restaurant?.id) === 28;
      if (isRancho28 && !result?.duplicate_prevented) {
        try {
          console.log('[RANCHO_PRINT_TABLES] resolved ids — numeric id:', createdOrderId, ' uuid (tracking_token):', createdOrderUuid);
          console.log('[RANCHO_PRINT_TABLES] Creating print job for order', createdOrderId);
          // SIN existence check bloqueante. Vamos directo al INSERT.
          // La dedupe se maneja con UNIQUE INDEX en Supabase si se
          // requiere (ver SQL al final del cambio).
          {
            // Construimos el payload SIN dependencia obligatoria de
            // fetches con joins. Si el fetch falla, igual insertamos
            // con la info que ya tenemos (cart, payload, response,
            // selectedTable, customerInfo).
            // 1) Fetch simple de la orden (SIN joins, .maybeSingle())
            let fullOrder: any = null;
            try {
              const {
                data: ord,
                error: ordErr
              } = await supabase.from('orders').select('*').eq('id', createdOrderId).maybeSingle();
              if (ordErr) {
                console.warn('[RANCHO_PRINT_TABLES] order fetch error (non-blocking)', ordErr);
              } else {
                fullOrder = ord;
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] order fetch threw (non-blocking)', e);
            }
            // 2) Fetch simple de order_items (SIN joins). Si falla,
            //    fallback al cart actual (ya tenemos los productos).
            let orderItems: any[] = [];
            try {
              const {
                data: items,
                error: itemsErr
              } = await supabase.from('order_items').select('*').eq('order_id', createdOrderId);
              if (itemsErr) {
                console.warn('[RANCHO_PRINT_TABLES] items fetch error (non-blocking)', itemsErr);
              } else if (items && items.length > 0) {
                orderItems = items;
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] items fetch threw (non-blocking)', e);
            }
            // 3) Construir el payload de items para el print job
            let itemsPayload: any[] = [];
            if (orderItems.length > 0) {
              // Si logramos leer de la DB, usamos eso (es la fuente de verdad)
              itemsPayload = orderItems.map(it => ({
                menu_item_id: it.menu_item_id,
                item_name_snapshot: it.item_name_snapshot,
                unit_price_snapshot: it.unit_price_snapshot,
                quantity: it.quantity,
                notes: it.notes,
                station_code: it.station_code,
                line_total: it.line_total,
                selected_options: [] // Omitimos opciones por simplicidad en el fallback
              }));
            } else {
              // Fallback al cart local si la DB falló
              itemsPayload = cart.map(ci => {
                const isCustomArmaItem = typeof ci.menuItem.id === 'string' && (ci.menuItem.id as string).startsWith('__custom__');
                const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
                const unitPrice = isCustomArmaItem ? 0 : Number(ci.menuItem.price) || 0;
                const lineTotal = isCustomArmaItem ? 0 : (unitPrice + optTotal) * ci.quantity;
                return {
                  menu_item_id: isCustomArmaItem ? null : ci.menuItem.id as string,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: lineTotal,
                  station_code: (ci.menuItem as any).station_code ?? null,
                  selected_options: (ci.options || []).map((o: any) => ({
                    option_group: o.option_group || '',
                    option_name: o.option_name || '',
                    price_delta: Number(o.price_delta) || 0
                  }))
                };
              });
            }
            const printPayload = {
              trigger: 'rancho_new',
              order_id: createdOrderId,
              order_number: createdOrderNumber,
              tracking_token: createdOrderUuid,
              created_at: fullOrder?.created_at || new Date().toISOString(),
              customer_name: fullOrder?.customer_name || payload.customer_name,
              customer_phone: fullOrder?.customer_phone || payload.customer_phone,
              delivery_type: fullOrder?.delivery_type || payload.delivery_type,
              payment_method: fullOrder?.payment_method || payload.payment_method,
              table_id: fullOrder?.table_id || payload.table_id,
              notes: fullOrder?.notes || payload.notes,
              subtotal: fullOrder?.subtotal || payload.subtotal,
              delivery_fee: fullOrder?.delivery_fee || payload.delivery_fee,
              total: fullOrder?.total || payload.total,
              source: fullOrder?.source || payload.source,
              items: itemsPayload
            };
            console.log('[RANCHO_PRINT_TABLES] Inserting print job with payload:', printPayload);
            const {
              error: pjError
            } = await supabase.from('order_print_jobs').insert({
              restaurant_id: 28,
              order_id: createdOrderUuid,
              order_number: createdOrderNumber,
              status: 'pending',
              payload: printPayload
            });
            if (pjError) {
              console.error('[RANCHO_PRINT_TABLES] Failed to insert print job:', pjError);
            } else {
              console.log('[RANCHO_PRINT_TABLES] Print job inserted successfully');
            }
          }
        } catch (printErr) {
          console.error('[RANCHO_PRINT_TABLES] Unhandled exception creating print job:', printErr);
        }
      }
      // Success! Clear cart and go back to step 1
      setCart([]);
      setSelectedTable(null);
      setOrderMode('mesa');
      setCustomerInfo(EMPTY_CUSTOMER_INFO);
      setStep(1);
      if (restaurantId && typeof window !== 'undefined') {
        const key = getStorageKey(restaurantId, effectiveMode, selectedTable?.id);
        localStorage.removeItem(key);
      }
      // Si el restaurante tiene el flag para volver a mesas, navegamos
      // a la grilla de Mesas del MISMO restaurante DENTRO del admin.
      // ⚠️ FIX: antes navegaba a `/r/${slug}/tables` — un prefijo de
      // ruta pública que NO existe en el router, así que caía al
      // catch-all del marketplace y terminaba redirigiendo a "/"
      // (noizacr.com). La ruta correcta de la vista de mesas es
      // `/admin/:slug/tables` (ver App.tsx). Si el slug faltara por
      // alguna razón, nos quedamos en /admin/<slug>/waiter como
      // fallback seguro — nunca al marketplace.
      if (returnsToTablesAfterSubmit && restaurant?.slug) {
        navigate(`/admin/${restaurant.slug}/tables`);
      }
    } catch (err: any) {
      console.error('[WAITER POS] Submit error:', err);
      // Sin respuesta del servidor (tiempo agotado o red caída): el estado
      // de la comanda es INCIERTO — pudo haber entrado o no. Reenviar a
      // ciegas duplicaría los productos en la cuenta de la mesa, así que
      // se pide verificar primero en la pantalla de Mesas.
      const isNoResponse = err?.name === 'AbortError' || String(err?.message || '').toLowerCase().includes('failed to fetch');
      if (isNoResponse) {
        setSubmitError('No hubo respuesta del servidor. LA COMANDA PUEDE NO HABERSE ' + 'ENVIADO. Revisá la mesa en la pantalla de Mesas antes de ' + 'volver a enviarla: si ya aparece con el pedido, NO la reenvíes ' + '(se duplicarían los productos). Si la mesa está libre, volvé a ' + 'enviarla — el pedido quedó guardado acá.');
      } else {
        setSubmitError(err.message || 'Error de conexión');
      }
    } finally {
      setIsSubmitting(false);
      // Liberar el gate síncrono. Siempre se ejecuta porque solo
      // entramos al try después de pasar el gate. Las validaciones
      // tempranas (que returnean antes del gate) ni siquiera setean
      // el ref, así que no necesitan liberarlo.
      submittingRef.current = false;
    }
  };
  if (!restaurant) return null;
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-6778" data-mp="106u1cwntdb50">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm" data-id="element-6779" data-mp="146w1309znuhn">
        <div className="flex items-center gap-3" data-id="element-6780" data-mp="cgzm68g0vahl">
          {isNatuCafe && restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100 bg-white" onError={e => {
          ;
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }} data-id="element-6781" data-mp="gictkmyrjlqm" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center text-white font-bold" data-id="element-6782" data-mp="74kspttfd0hj">
              {restaurant.name.charAt(0)}
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-6783" data-mp="g3g03987ggmf">
            {restaurant.name}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-gray-500" data-id="element-6784" data-mp="b7h4p1oglyci">
          <span className={cn(step >= 1 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6785" data-mp="q2590ci4iqqj">
            1{' '}
            {supportsMultiMode && orderMode === 'pickup' ? 'Llevar' : supportsMultiMode && orderMode === 'delivery' ? 'Express' : 'Mesa'}
          </span>
          <span data-id="element-6786" data-mp="12cwk4w0h8u1m">·</span>
          <span className={cn(step >= 2 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6787" data-mp="12cmoarsr3so0">
            2 Productos
          </span>
          <span data-id="element-6788" data-mp="17e2bapeho991">·</span>
          <span className={cn(step >= 3 ? 'text-brand-charcoal font-bold' : '')} data-id="element-6789" data-mp="sn34njx7rus3">
            3 Confirmar
          </span>
        </div>

        <button onClick={async () => {
        // Rancho San Francisco (SOLO mesero): "Salir" desde una mesa
        // NO debe cerrar sesión. El mesero trabaja toda su jornada
        // entrando y saliendo de mesas; cerrar sesión cada vez lo
        // obligaba a re-loguearse. Acá "Salir" lo devuelve EXACTAMENTE
        // a /admin/rancho-san-francisco/waiter (su pantalla principal,
        // la grilla de Mesas / step 1) MANTENIENDO la sesión activa —
        // a ningún otro lugar.
        //
        // ⚠️ Como YA estamos montados en /admin/<slug>/waiter, un
        // navigate() a la misma ruta no remonta el componente ni
        // resetea el estado en memoria — por eso el botón "no hacía
        // nada". Reseteamos el estado de la página a mano (mismo
        // patrón que el post-submit) para volver a la pantalla
        // principal, y luego garantizamos que la URL sea exactamente
        // /admin/rancho-san-francisco/waiter. Scope estricto: solo
        // Rancho + role='waiter'; ningún otro restaurante ni rol
        // cambia.
        if (isRanchoSanFrancisco && role === 'waiter') {
          setCart([]);
          setSelectedTable(null);
          setOrderMode('mesa');
          setCustomerInfo(EMPTY_CUSTOMER_INFO);
          setActiveCategoryId(null);
          setSearchQuery('');
          setStep(1);
          navigate('/admin/rancho-san-francisco/waiter');
          return;
        }
        // Meseros (role='waiter'): "Salir" cierra sesión de verdad
        // y los manda a la pantalla de login del restaurante. No
        // tienen permiso para ver el selector de estaciones ni
        // navegar a otras áreas del admin.
        if (role === 'waiter') {
          try {
            await signOut();
          } catch (e) {
            console.warn('[WaiterPOS] signOut error (non-blocking):', e);
          }
          // Hard redirect para garantizar que se limpie todo el
          // estado en memoria (carrito persistido, contexts, etc.).
          const slug = restaurant?.slug || 'rancho-san-francisco';
          window.location.href = `/admin/${slug}/login`;
          return;
        }
        // NatuCafé — el dueño quiere que "Salir" lo regrese directo
        // a la grilla de Mesas (su pantalla principal). El resto de
        // restaurantes mantienen el comportamiento original: vuelven
        // a /operacion para que el operador pueda cambiar de estación.
        if (isNatuCafe && restaurant?.slug) {
          navigate(`/admin/${restaurant.slug}/tables`);
        } else {
          navigate(`/admin/${restaurant.slug}/operacion`);
        }
      }} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" title={role === 'waiter' && !isRanchoSanFrancisco ? 'Cerrar sesión' : 'Salir'} data-id="element-6790" data-mp="xkli9nm6ey6f">
          <LogOut className="w-4 h-4" data-id="element-6791" data-mp="omqdaprc272h" />
          <span className="hidden sm:inline" data-id="element-6792" data-mp="koam09o7ycj9">
            {role === 'waiter' && !isRanchoSanFrancisco ? 'Cerrar sesión' : 'Salir'}
          </span>
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-6793" data-mp="tmuxoqvd3lxz">
        {step === 1 && <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-6794" data-mp="5x0lmbiva1es">
            <div className="max-w-6xl mx-auto" data-id="element-6795" data-mp="10a37hjlatq7">
              {/* Mode selector (Mesa / Para llevar / Express) — Cabaña + NatuCafé */}
              {supportsMultiMode && <div className="mb-8" data-id="element-6796" data-mp="2hdbyr4m6g9qn">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-6797" data-mp="2hedkc30tkemm">
                    Tipo de orden
                  </h2>
                  <div className="grid grid-cols-3 gap-2 max-w-2xl" data-id="element-6798" data-mp="28l0k0g0feaqi">
                    {([{
                key: 'mesa',
                label: 'Mesa',
                emoji: '🍽️'
              }, {
                key: 'pickup',
                label: 'Para llevar',
                emoji: '🥡'
              }, {
                key: 'delivery',
                label: 'Express',
                emoji: '🛵'
              }] as {
                key: WaiterOrderMode;
                label: string;
                emoji: string;
              }[]).map(mode => <button key={mode.key} onClick={() => {
                setOrderMode(mode.key);
                if (mode.key !== 'mesa') {
                  setSelectedTable(null);
                }
              }} className={cn('flex flex-col items-center justify-center gap-1 py-4 px-3 rounded-2xl border-2 font-bold transition-all', orderMode === mode.key ? 'bg-brand-charcoal text-white border-brand-charcoal shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400')} data-id="element-6799" data-mp="1v4aa73pox7aj">
                        <span className="text-2xl leading-none" data-id="element-6800" data-mp="teepunulpd1u">
                          {mode.emoji}
                        </span>
                        <span className="text-sm" data-id="element-6801" data-mp="9vr71ctvo81w">{mode.label}</span>
                      </button>)}
                  </div>
                </div>}

              {/* Customer info form (Para llevar / Express) — Cabaña + NatuCafé */}
              {supportsMultiMode && orderMode !== 'mesa' && <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-2xl" data-id="element-6802" data-mp="ss8uhi1p1lsc">
                  <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-6803" data-mp="nu7amuwg1ift">
                    {orderMode === 'delivery' ? 'Datos del cliente (Express)' : 'Datos del cliente (Para llevar)'}
                  </h2>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-6804" data-mp="xllhxoumzmji">
                    {orderMode === 'delivery' ? 'Información que vas a anotar de la llamada para el repartidor.' : 'Información para identificar la orden cuando el cliente venga a retirarla.'}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6805" data-mp="95vhzm6lnh11">
                    <div className="sm:col-span-1" data-id="element-6806" data-mp="93rev6giym1t">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6807" data-mp="d776fodqlw7n">
                        Nombre del cliente *
                      </label>
                      <input type="text" value={customerInfo.name} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  name: e.target.value
                }))} placeholder="Ej: María Pérez" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6808" data-mp="i00asb5an2j7" />
                    </div>

                    <div className="sm:col-span-1" data-id="element-6809" data-mp="2v6qbawxe7mze">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6810" data-mp="2qddl4id2f84j">
                        Teléfono {orderMode === 'delivery' && '*'}
                      </label>
                      <input type="tel" inputMode="tel" value={customerInfo.phone} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} placeholder="8888-8888" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6811" data-mp="2hea2ffht8uqk" />
                    </div>

                    {orderMode === 'delivery' && <>
                        <div className="sm:col-span-2" data-id="element-6812" data-mp="3hd9fbddbq3ig">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6813" data-mp="33ktcackfa5hj">
                            Dirección de entrega *
                          </label>
                          <textarea value={customerInfo.address} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} placeholder="Ej: 200m sur de la iglesia, casa portón verde…" rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-6814" data-mp="37kbvn3wli8i9" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-6815" data-mp="23epj7omxbbg2">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6816" data-mp="2hbcruoo8nq1t">
                            Link de Google Maps (opcional)
                          </label>
                          <input type="url" inputMode="url" value={customerInfo.maps_url} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    maps_url: e.target.value
                  }))} placeholder="https://maps.google.com/…" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6817" data-mp="1on77i313k32i" />
                        </div>

                        {isNatuCafe ? <>
                            <div className="sm:col-span-1" data-id="element-6818" data-mp="3ersigoje8bxh">
                              <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6819" data-mp="3er0otj9cwo9u">
                                Zona de entrega *
                              </label>
                              <select value={selectedMsZone} onChange={e => {
                      setSelectedMsZone(e.target.value);
                      setSelectedMsSubzone('');
                      setSelectedMsFee(0);
                      setSelectedMsCatalogId(null);
                    }} disabled={msZonesLoading} className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent disabled:opacity-50" data-id="element-6820" data-mp="2n1fgikokfveq">
                                <option value="" data-id="element-6821" data-mp="7nvevqf7vqtl">Seleccionar zona...</option>
                                {msZoneNames.map(z => <option key={z} value={z} data-id="element-6822" data-mp="2pbtps8g6j2a">
                                    {z}
                                  </option>)}
                              </select>
                            </div>
                            <div className="sm:col-span-1" data-id="element-6823" data-mp="3ku5b5w0ud006">
                              <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6824" data-mp="3kvq6voyw6ozm">
                                Localidad *
                              </label>
                              <select value={selectedMsSubzone} onChange={e => {
                      const sub = e.target.value;
                      setSelectedMsSubzone(sub);
                      const match = msSubzonesForZone.find(z => z.subzone === sub);
                      if (match) {
                        setSelectedMsFee(match.fee);
                        setSelectedMsCatalogId(match.id);
                      } else {
                        setSelectedMsFee(0);
                        setSelectedMsCatalogId(null);
                      }
                    }} disabled={!selectedMsZone || msZonesLoading} className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent disabled:opacity-50" data-id="element-6825" data-mp="1j3tukd4whb3s">
                                <option value="" data-id="element-6826" data-mp="3e644ekk0i9v6">
                                  Seleccionar localidad...
                                </option>
                                {msSubzonesForZone.map(z => <option key={`${z.id}-${z.subzone}`} value={z.subzone} data-id="element-6827" data-mp="3n3xw1i6u0fc0">
                                    {z.subzone}
                                  </option>)}
                              </select>
                              {selectedMsSubzone && <p className="mt-1.5 text-xs font-bold text-green-600" data-id="element-6828" data-mp="alzpsfp6ardl">
                                  Costo de envío:{' '}
                                  {formatCurrency(selectedMsFee)} — automático
                                </p>}
                            </div>
                          </> : <div className="sm:col-span-2" data-id="element-6829" data-mp="1gf31ur2hhb7i">
                            <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6830" data-mp="1bncla3cydr7n">
                              Monto de express (opcional)
                            </label>
                            <div className="relative" data-id="element-6831" data-mp="12mwo1bmszgb8">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold pointer-events-none" data-id="element-6832" data-mp="12ohhsz1amv81">
                                ₡
                              </span>
                              <input type="number" inputMode="decimal" min="0" step="100" value={customerInfo.express_fee} onChange={e => setCustomerInfo(prev => ({
                      ...prev,
                      express_fee: e.target.value
                    }))} placeholder="Ej: 1500" className="w-full h-11 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6833" data-mp="sp8qkdxtbupd" />
                            </div>
                            <p className="mt-1.5 text-xs text-gray-500" data-id="element-6834" data-mp="2gm85lid36pq6">
                              Se sumará al total de la orden si se ingresa.
                            </p>
                          </div>}
                      </>}

                    <div className="sm:col-span-2" data-id="element-6835" data-mp="vd9666fuqh2">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-6836" data-mp="u27cbwjzssl">
                        Notas (opcional)
                      </label>
                      <textarea value={customerInfo.notes} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  notes: e.target.value
                }))} placeholder="Ej: tocar el timbre, sin cebolla, etc." rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-6837" data-mp="32eakfz087ecz" />
                    </div>
                  </div>

                  <button onClick={() => {
              if (!customerInfo.name.trim()) return;
              if (orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim())) return;
              if (isNatuCafe && orderMode === 'delivery' && (!selectedMsZone || !selectedMsSubzone)) return;
              setStep(2);
            }} disabled={!customerInfo.name.trim() || orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim() || isNatuCafe && (!selectedMsZone || !selectedMsSubzone))} className={cn('mt-5 w-full h-12 rounded-xl font-bold text-base transition-all', customerInfo.name.trim() && (orderMode !== 'delivery' || customerInfo.address.trim() && customerInfo.phone.trim() && (!isNatuCafe || selectedMsZone && selectedMsSubzone)) ? 'bg-green-500 hover:bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-6838" data-mp="25ts5g05moo85">
                    Continuar a productos →
                  </button>
                </div>}

              {/* Tables grid — only when in 'mesa' mode (or any non-Cabaña restaurant) */}
              {(!supportsMultiMode || orderMode === 'mesa') && <>
                  <h1 className="text-3xl font-black text-gray-900 mb-2" data-id="element-6839" data-mp="2sqlve52h834o">
                    Selecciona una mesa
                  </h1>
                  <p className="text-gray-500 mb-8" data-id="element-6840" data-mp="145es2hbsm8wx">
                    Toca una mesa para tomar la orden
                  </p>

                  {tablesLoading ? <div className="flex items-center justify-center py-20" data-id="element-6841" data-mp="1879rthe2se8l">
                      <div className="w-8 h-8 border-4 border-brand-charcoal border-t-transparent rounded-full animate-spin" data-id="element-6842" data-mp="1d5b9pyzrm7ih"></div>
                    </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-6843" data-mp="1mwpfgoib83k2">
                      {sortedTables.map(table => {
                const isOccupied = table.status !== 'free';
                return <button key={table.id} onClick={() => handleTableSelect(table)} className={cn('flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all min-h-[140px]', isOccupied ? 'bg-amber-50 border-amber-200 hover:border-amber-400' : 'bg-white border-gray-100 hover:border-green-500 hover:shadow-md')} data-id="element-6844" data-mp="20vfdc6cye79h">
                            <span className="text-5xl font-black text-gray-900 mb-2" data-id="element-6845" data-mp="1dtnku5ov0zkq">
                              {table.table_number}
                            </span>
                            {table.table_name && <span className="text-xs font-medium text-gray-500 mb-3 text-center line-clamp-1" data-id="element-6846" data-mp="1nr9ykoufal34">
                                {table.table_name}
                              </span>}
                            <div className={cn('px-3 py-1 rounded-full text-xs font-bold', isOccupied ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')} data-id="element-6847" data-mp="1xixvv6dnuwci">
                              {isOccupied ? 'Ocupada' : 'Libre'}
                            </div>
                          </button>;
              })}
                    </div>}
                </>}
            </div>
          </div>}

        {step === 2 && (selectedTable || supportsMultiMode && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-6848" data-mp="2c666o6324zny">
              {/* Sub-header */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0" data-id="element-6849" data-mp="1xdujj67budlx">
                <div className="flex items-center gap-3" data-id="element-6850" data-mp="1of959n8w1kqy">
                  <button onClick={() => setStep(1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6851" data-mp="1ofsi4pjoliy4">
                    <ArrowLeft className="w-6 h-6" data-id="element-6852" data-mp="28uaifi8om197" />
                  </button>
                  <div data-id="element-6853" data-mp="23xtk5v7rq6ie">
                    <h2 className="text-xl font-bold text-gray-900 leading-none" data-id="element-6854" data-mp="23vfslwcarcfl">
                      {selectedTable ? `Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Express — ${customerInfo.name || 'Cliente'}` : `Para llevar — ${customerInfo.name || 'Cliente'}`}
                    </h2>
                    {selectedTable && selectedTable.table_name && <span className="text-sm text-gray-500" data-id="element-6855" data-mp="1g7z7perd2jyo">
                        {selectedTable.table_name}
                      </span>}
                    {!selectedTable && customerInfo.phone && <span className="text-sm text-gray-500" data-id="element-6856" data-mp="127wtc2i9i8o6">
                        📞 {customerInfo.phone}
                      </span>}
                  </div>
                </div>

                <button onClick={() => setStep(3)} disabled={cart.length === 0} className={cn('flex items-center gap-3 px-4 py-2 rounded-xl font-bold transition-all', cart.length > 0 ? 'bg-brand-charcoal text-white hover:bg-gray-800 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-6857" data-mp="3hwlm4rvvj3mh">
                  <div className="flex items-center gap-2" data-id="element-6858" data-mp="2z4b0sbx9joc4">
                    <ShoppingBag className="w-5 h-5" data-id="element-6859" data-mp="344fg3ymfqd0r" />
                    <span className="bg-white/20 px-2 py-0.5 rounded-md text-sm" data-id="element-6860" data-mp="38w6xbijpb6e8">
                      {cartItemCount}
                    </span>
                  </div>
                  <span data-id="element-6861" data-mp="2bgjd6c69vovj">{formatCurrency(orderTotal)}</span>
                </button>
              </div>

              {/* Search */}
              <div className="bg-white border-b border-gray-100 px-4 py-3 shrink-0" data-id="element-6862" data-mp="2l9ybgquv5fat">
                <div className="relative max-w-2xl mx-auto" data-id="element-6863" data-mp="2lcb1zseosxzh">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-6864" data-mp="2q9t0spyzpwtr" />
                  <input type="text" inputMode="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar producto…" className="w-full h-12 pl-12 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-6865" data-mp="2va7a0bcbvctd" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full" aria-label="Limpiar búsqueda" data-id="element-6866" data-mp="3nxl0bqlita4i">
                      <X className="w-4 h-4" data-id="element-6867" data-mp="1cbj41ffiyo9q" />
                    </button>}
                </div>
              </div>

              {/* Categories — hide while searching */}
              {!searchQuery.trim() && <div className="bg-white border-b border-gray-100 overflow-x-auto shrink-0 hide-scrollbar" data-id="element-6868" data-mp="1zyr7drte7n94">
                  <div className="flex p-2 gap-2 min-w-max" data-id="element-6869" data-mp="1uzeccjsdb9vg">
                    {displayCategories.map(cat => {
              const isArmaTab = cat.id === ARMA_TU_PLATO_ID;
              return <button key={cat.id} onClick={() => setActiveCategoryId(cat.id)} className={cn('px-6 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap', activeCategoryId === cat.id ? isArmaTab ? 'bg-amber-500 text-white' : 'bg-brand-charcoal text-white' : isArmaTab ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100')} data-id="element-6870" data-mp="1l938ex8op0ok">
                          {cat.name}
                        </button>;
            })}
                  </div>
                </div>}

              {/* Items Grid */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-6871" data-mp="3kgjlbya8zoqr">
                {armaTuPlatoEnabled && !searchQuery.trim() && activeCategoryId === ARMA_TU_PLATO_ID ? <ArmaTuPlatoPanel lines={armaLines} setLines={setArmaLines} onAddToCart={filledLines => {
            const newCartItems: WaiterCartItem[] = filledLines.map(text => {
              const syntheticId = `__custom__${crypto.randomUUID()}`;
              return {
                id: crypto.randomUUID(),
                menuItem: {
                  id: syntheticId,
                  name: text,
                  price: 0,
                  is_active: true,
                  is_customizable: false,
                  category_id: ARMA_TU_PLATO_ID,
                  restaurant_id: Number(restaurantId) || 0
                } as any,
                quantity: 1,
                options: [],
                notes: ''
              };
            });
            setCart(prev => [...prev, ...newCartItems]);
            setArmaLines(['', '', '', '', '', '']);
          }} data-id="element-6872" data-mp="1ipyeqrt9hsoo" /> : (() => {
            const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());
            const filteredItems = q ? activeItems.filter(item => {
              const haystack = normalize(`${item.name} ${item.description || ''}`);
              return haystack.includes(q);
            }) : activeItems.filter(item => item.category_id === activeCategoryId);
            if (filteredItems.length === 0) {
              return <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-6873" data-mp="3eqgzea1b1xhj">
                          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-6874" data-mp="39rxco7ew0ho4">
                            <Search className="w-7 h-7 text-gray-400" data-id="element-6875" data-mp="30tbvafl5j8ox" />
                          </div>
                          <p className="text-lg font-bold text-gray-900 mb-1" data-id="element-6876" data-mp="2vtpa9jz30o5k">
                            {q ? 'Sin resultados' : 'No hay productos'}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-6877" data-mp="2m45ni3890a5f">
                            {q ? `No encontramos productos para "${searchQuery}"` : 'No hay productos en esta categoría'}
                          </p>
                        </div>;
            }
            const activeCat = categories.find(c => c.id === activeCategoryId);
            const isPizzaCat = activeCat?.name?.toLowerCase().includes('pizza');
            return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-6878" data-mp="1ojmk21rq6bni">
                        {isEmilius && isPizzaCat && !q && <button key="arma-tu-pizza" onClick={() => setShowEmiliusArmaTuPizzaModal(true)} className="bg-[#111] rounded-2xl p-4 shadow-sm border border-[#6FA248]/30 flex flex-col h-full hover:border-[#6FA248] transition-all text-left group" data-id="element-6879" data-mp="2h7ibos2ftddf">
                            <div className="flex-1" data-id="element-6880" data-mp="2cegz4qppdpx0">
                              <h3 className="font-bold text-white text-lg leading-tight mb-1" data-id="element-6881" data-mp="30533a6wl6q1q">
                                Armá tu propia pizza
                              </h3>
                              <p className="text-sm text-white/70 line-clamp-2 mb-3" data-id="element-6882" data-mp="thcazpu7fkh9">
                                Elegí el tamaño y combiná dos especialidades
                                diferentes (mitad y mitad).
                              </p>
                            </div>
                            <div className="flex items-end justify-between mt-4" data-id="element-6883" data-mp="y8u5pw5mwots">
                              <span className="font-bold text-sm text-[#6FA248]" data-id="element-6884" data-mp="aizxxt069qge">
                                Armar ahora
                              </span>
                              <div className="w-8 h-8 rounded-full bg-[#6FA248]/20 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6885" data-mp="5lhn1x2lh15r">
                                <span className="text-lg" data-id="element-6886" data-mp="ekbqo7ecerst">🍕</span>
                              </div>
                            </div>
                          </button>}
                        {filteredItems.map(item => {
                const cartItem = cart.find(c => c.menuItem.id === item.id);
                return <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col h-full" data-id="element-6887" data-mp="2f1h8qw62zaf0">
                              <div className="flex-1" data-id="element-6888" data-mp="2nv43qk2gs9n5">
                                <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 line-clamp-2" data-id="element-6889" data-mp="2srkpx8q3sdxl">
                                  {item.name}
                                </h3>
                                {item.description && <p className="text-sm text-gray-500 line-clamp-2 mb-3" data-id="element-6890" data-mp="36s57y2o725q1">
                                    {item.description}
                                  </p>}
                              </div>
                              <div className="flex items-end justify-between mt-4" data-id="element-6891" data-mp="1cevww6g02c5o">
                                <span className="font-bold text-xl text-green-600" data-id="element-6892" data-mp="14vmy1754dvuv">
                                  {formatCurrency(Number(item.price))}
                                </span>

                                {isCabanaParrillera && selectedTable ? <button onClick={() => setSelectedProduct(item)} aria-label={`Agregar ${item.name} a la orden`} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-6893" data-mp="r3ghhjy21muw">
                                    <Plus className="w-6 h-6" data-id="element-6894" data-mp="19u7quli1ld9a" />
                                  </button> : cartItem && !item.is_customizable &&
                    // Rancho San Francisco (id 28) — el cajero
                    // necesita poder agregar OTRA línea del mismo
                    // producto con notas distintas (ej. dos
                    // Gaseosas, una "fría sin hielo" y otra "con
                    // limón"). Si forzáramos el stepper -/qty/+
                    // como en los demás restaurantes, la segunda
                    // Gaseosa simplemente incrementa la primera
                    // y comparte sus notas — sin manera de
                    // diferenciarlas en la comanda.
                    //
                    // Fix: en Rancho, NUNCA usamos el stepper
                    // rápido. Cada click en "+" reabre el modal
                    // de detalle para que el cajero ponga notas/
                    // opciones nuevas si quiere. Si no pone
                    // notas, el modal seguirá deduplicando la
                    // línea (lógica existente de addToCart) —
                    // pero con notas distintas crea una línea
                    // nueva. Cero impacto fuera de Rancho.
                    !isRanchoSanFrancisco ? <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-6895" data-mp="3s1wnrclwzmob">
                                    <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, -1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-6896" data-mp="3e25f7re62yhp">
                                      <Minus className="w-4 h-4" data-id="element-6897" data-mp="139arvdbbkcts" />
                                    </button>
                                    <span className="w-8 text-center font-bold text-gray-900" data-id="element-6898" data-mp="2g9fgo4yrt9qz">
                                      {cartItem.quantity}
                                    </span>
                                    <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, 1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-6899" data-mp="278zc0s6ajwkj">
                                      <Plus className="w-4 h-4" data-id="element-6900" data-mp="2q6k385wj378b" />
                                    </button>
                                  </div> : <button onClick={() => setSelectedProduct(item)} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-6901" data-mp="3ip7jn80kxy32">
                                    <Plus className="w-6 h-6" data-id="element-6902" data-mp="3q7xbvmr9fcf5" />
                                  </button>}
                              </div>
                            </div>;
              })}
                      </div>;
          })()}
              </div>
            </div>}

        {step === 3 && (selectedTable || supportsMultiMode && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden bg-white" data-id="element-6903" data-mp="hsndaomkwpc4">
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0" data-id="element-6904" data-mp="mpnbkdd6snso">
                <button onClick={() => setStep(2)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6905" data-mp="qu4optp1k2uh">
                  <ArrowLeft className="w-6 h-6" data-id="element-6906" data-mp="i333umkmwo9f" />
                </button>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-6907" data-mp="e2jbdwb0a8tm">
                  {selectedTable ? `Confirmar Orden - Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Confirmar Express — ${customerInfo.name || 'Cliente'}` : `Confirmar Para Llevar — ${customerInfo.name || 'Cliente'}`}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-6908" data-mp="3rbxhpfws17ys">
                <div className="max-w-3xl mx-auto" data-id="element-6909" data-mp="951224t2trl3">
                  {submitError && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800" data-id="element-6910" data-mp="44d93dlaf0o8">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-6911" data-mp="1khjzjftshmpn" />
                      <p className="text-sm font-medium" data-id="element-6912" data-mp="1fjickjt8t38w">{submitError}</p>
                    </div>}

                  <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden mb-6" data-id="element-6913" data-mp="2xglq1ehl9eww">
                    <div className="divide-y divide-gray-100" data-id="element-6914" data-mp="32ib1gv8q3uli">
                      {cart.map(item => {
                  const optionsTotal = item.options.reduce((sum, opt) => sum + (Number(opt.price_delta) || 0), 0);
                  const itemTotal = (Number(item.menuItem.price) + optionsTotal) * item.quantity;
                  return <div key={item.id} className="p-4 flex gap-4" data-id="element-6915" data-mp="37fuks9ka2xkb">
                            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 h-fit shrink-0" data-id="element-6916" data-mp="3cdwc1hruy36v">
                              <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-6917" data-mp="3xaz5pw3upyh">
                                <Minus className="w-4 h-4" data-id="element-6918" data-mp="dob4y510qggh" />
                              </button>
                              <span className="w-8 text-center font-bold text-gray-900" data-id="element-6919" data-mp="8mlq0eaodin4">
                                {item.quantity}
                              </span>
                              <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-6920" data-mp="3vsuw0k4ggo85">
                                <Plus className="w-4 h-4" data-id="element-6921" data-mp="3h3qel55hio13" />
                              </button>
                            </div>

                            <div className="flex-1 min-w-0" data-id="element-6922" data-mp="3c4vmtdpjhp4u">
                              <div className="flex justify-between items-start gap-4" data-id="element-6923" data-mp="3830kc5vvh5ph">
                                <h4 className="font-bold text-gray-900 text-lg" data-id="element-6924" data-mp="385ed2832y5tv">
                                  {item.menuItem.name}
                                </h4>
                                <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-6925" data-mp="1rl07xs5zua3p">
                                  {formatCurrency(itemTotal)}
                                </span>
                              </div>

                              {item.options.length > 0 && <ul className="mt-1 space-y-0.5" data-id="element-6926" data-mp="13ybhuj6tzbc0">
                                  {aggregateQuantityOptions(item.options).map((opt, idx) => <li key={idx} className="text-sm text-gray-500 flex justify-between" data-id="element-6927" data-mp="1dtlmzfsizyu0" data-mpi={idx}>
                                        <span data-id="element-6928" data-mp="l4emwcdfb16n">
                                          • {opt.option_name}
                                          {opt.qty && opt.qty > 1 ? ` x ${opt.qty}` : ''}
                                        </span>
                                        {Number(opt.price_delta) > 0 && <span data-id="element-6929" data-mp="3jp91unyjheli">
                                            +
                                            {formatCurrency(Number(opt.price_delta))}
                                          </span>}
                                      </li>)}
                                </ul>}

                              {item.notes && <p className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg" data-id="element-6930" data-mp="l5ai0859nyq8">
                                  Nota: {item.notes}
                                </p>}
                            </div>

                            <button onClick={() => removeItem(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-fit transition-colors" data-id="element-6931" data-mp="14s41wb3jrqs4">
                              <X className="w-5 h-5" data-id="element-6932" data-mp="h44812axvk9b" />
                            </button>
                          </div>;
                })}
                    </div>

                    <div className="p-4 bg-white border-t border-gray-100 space-y-2" data-id="element-6933" data-mp="75wxp2b5o0ga">
                      {expressFeeAmount > 0 && <>
                          <div className="flex justify-between items-center text-base" data-id="element-6934" data-mp="c2nbtk3eglz2">
                            <span className="text-gray-500" data-id="element-6935" data-mp="3ub60okxziv78">Subtotal</span>
                            <span className="font-medium text-gray-700" data-id="element-6936" data-mp="25ip9j5n6ohq">
                              {formatCurrency(cartTotal)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-base" data-id="element-6937" data-mp="18u7mq7r9weer">
                            <span className="text-gray-500" data-id="element-6938" data-mp="13vecco7a93it">
                              Monto de express
                            </span>
                            <span className="font-medium text-gray-700" data-id="element-6939" data-mp="yz7e7hl4owbf">
                              {formatCurrency(expressFeeAmount)}
                            </span>
                          </div>
                          <div className="border-t border-gray-100 pt-2" data-id="element-6940" data-mp="1rnv27kx9hzso" />
                        </>}
                      <div className="flex justify-between items-center" data-id="element-6941" data-mp="1hw8kd5qf52gy">
                        <span className="text-lg font-medium text-gray-500" data-id="element-6942" data-mp="1htur92fau0c1">
                          Total a cobrar
                        </span>
                        <span className="text-3xl font-black text-gray-900" data-id="element-6943" data-mp="2akcpnq52vdj4">
                          {formatCurrency(orderTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSubmitOrder} disabled={isSubmitting || cart.length === 0} className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-id="element-6944" data-mp="2mv434gawlsxd">
                    {isSubmitting ? <div className="flex items-center gap-2" data-id="element-6945" data-mp="30ssmtkt8iu0u">
                        <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" data-id="element-6946" data-mp="35s5a26o0q33y" />
                        <span data-id="element-6947" data-mp="3amj9ck5swudw">Enviando a cocina...</span>
                      </div> : <div className="flex items-center gap-2" data-id="element-6948" data-mp="3fldrrkzuwgxh">
                        <Check className="w-6 h-6" data-id="element-6949" data-mp="3kjxgtu9qy8u2" />
                        <span data-id="element-6950" data-mp="3ojy3g2701qc7">Confirmar y Enviar Orden</span>
                      </div>}
                  </Button>
                </div>
              </div>
            </div>}
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleAddToCart} data-id="element-6951" data-mp="1kejq69s5ti3k" />}

      {showEmiliusArmaTuPizzaModal && <EmiliusArmaTuPizzaModal isOpen={showEmiliusArmaTuPizzaModal} onClose={() => setShowEmiliusArmaTuPizzaModal(false)} onAdd={handleAddToCart} pizzas={items.filter(i => {
      const cat = categories.find(c => c.id === i.category_id);
      return cat?.name?.toLowerCase().includes('pizza');
    })} data-id="element-6952" data-mp="2855ze4dwh8f5" />}
    </div>;
}